import React, { useRef, useState } from 'react';
import Logo from '../../assets/logo-sm.png';
import { useAppSelector } from '../../app/hooks';
import SubmitButton from '../../components/submitButton';
import { useAsync } from 'react-async';
import cx from 'classnames';
import { ResponseStatus } from '../../components/notifications';
import { Redirect } from 'react-router';
import { checkPreviousPasswords, resetPassword } from '../../api/services/authService';
import CheckPreviousPasswordModel from '../../api/models/checkPreviousPasswordModel';
import { DataResponse } from '../../api/responses/baseResponse';

type Rule = {
  text: string;
  validation: () => boolean;
};

const ResetPassword = () => {
  const queryParams = new URLSearchParams(window.location.search);

  // Refs
  const emailRef = useRef<HTMLInputElement>(null);
  const newPasswordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);

  // State
  const [saved, setSaved] = useState(false);

  const [emailAddress, setEmailAddress] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const userState = useAppSelector((userState) => userState);
  const [isAlreadyBeenUsed, setIsAlreadyBeenUsed] = useState<boolean>(false);

  const checkPreviousPasswordsDefer = async () => {
    let req: CheckPreviousPasswordModel = {
      email: emailAddress,
      newPassword: newPassword
    };

    return checkPreviousPasswords(req);
  }

  const checkPreviousPasswordsRequest = useAsync({
    deferFn: checkPreviousPasswordsDefer,
    onResolve: (response: DataResponse<boolean>) => {
      setIsAlreadyBeenUsed(response.data);
    },
  });

  // Selectors
  const pendingLogin = useAppSelector((s) => s.user.status === 'logging_in');

  const save = useAsync({
    deferFn: async () => {
      await resetPassword({
        emailAddress: emailAddress,
        resetToken: queryParams.get('token') ?? '',
        newPassword: newPassword,
        confirmPassword: confirmPassword,
      });
    },
    onResolve: () => {
      setSaved(true);
    },
    onReject: () => {
      setSaved(false);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValid) {
      save.run();
    }
  };

  const rules: Rule[] = [
    {
      text: 'Between 8 and 20 characters',
      validation: () => {
        return newPassword.length >= 8 && newPassword.length <= 20;
      },
    },
    {
      text: 'Has uppercase character',
      validation: () => {
        return /[A-Z]/.test(newPassword);
      },
    },
    {
      text: 'Has lowercase character',
      validation: () => {
        return /[a-z]/.test(newPassword);
      },
    },
    {
      text: 'Has a digit',
      validation: () => {
        return /[0-9]/.test(newPassword);
      },
    },
    {
      text: 'Has special character',
      validation: () => {
        //eslint-disable-next-line
        return /[\s~`!@#$%\^&*+=\-\[\]\\';,/{}|\\":<>\?()\._]/g.test(
          newPassword
        );
      },
    },
    {
      text: 'Password & Confirm Password Match',
      validation: () => {
        return (
          newPassword === confirmPassword &&
          newPassword.length > 0 &&
          confirmPassword.length > 0
        );
      },
    },
    {
      text: 'Password cannot be one of the previous 4 passwords',
      validation: () => {
        return !isAlreadyBeenUsed && newPassword.length > 8;
      },
    },
  ];

  // Validation Rules
  const isValid = !rules.some((r) => r.validation() === false);
  if (save.isResolved && !saved) {
    return <Redirect to='/' />;
  }
  return (
    <div className='flex bg-ptsblue-normal flex-col w-full items-center justify-center'>
      <div className='text-gray-900 w-full p-6 md:max-w-xl'>
        <ResponseStatus state={save} />
        <form
          className='bg-white rounded-lg border shadow-lg p-10 mb-2'
          onSubmit={handleSubmit}
        >
          <img
            src={Logo}
            alt='Pediatric Therapeutic Services'
            className='object-contain object-center h-28 w-full mb-6'
          />

          <div className='mb-4'>
            <label className='block mb-2' htmlFor='emailaddress'>
              Email Address
            </label>
            <input
              id='emailaddress'
              onChange={(event) => setEmailAddress(event.target.value)}
              value={emailAddress}
              ref={emailRef}
              type='text'
              placeholder='Email Address'
            />
          </div>

          <div className='mb-6'>
            <label className='block mb-2' htmlFor='password'>
              New Password <span className='text-red-500 pl-1'>*</span>
            </label>
            <input
              id='newPassword'
              value={newPassword}
              ref={newPasswordRef}
              onChange={(event) => setNewPassword(event.target.value)}
              onBlur={() => checkPreviousPasswordsRequest.run()}
              type='password'
              placeholder='***************************'
            />
          </div>

          <div className='mb-6'>
            <label className='block mb-2' htmlFor='password'>
              Confirm Password
            </label>
            <input
              id='confirmPassword'
              type='password'
              value={confirmPassword}
              ref={confirmPasswordRef}
              onChange={(event) => setConfirmPassword(event.target.value)}
              placeholder='***************************'
            />
          </div>

          <div className='col-span-6 sm:col-span-6'>
            <ol>
              {rules.map((r) => (
                <li key={r.text} className='flex items-center mb-1'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    className={cx(
                      'h-6 w-6 mr-1',
                      r.validation() === true
                        ? 'text-ptsgreen-normal'
                        : 'text-gray-400'
                    )}
                    viewBox='0 0 20 20'
                    fill='currentColor'
                  >
                    <path
                      fillRule='evenodd'
                      d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                      clipRule='evenodd'
                    />
                  </svg>
                  <span className='text-sm'>{r.text}</span>
                </li>
              ))}
            </ol>
          </div>
          <div className='flex items-center justify-center'>
            <SubmitButton
              spinning={pendingLogin}
              text='Enter'
              disabled={!isValid}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
